import {
  TimeField,
  DateInput as AriaDateInput,
  DateSegment as AriaDateSegment,
  type TimeFieldProps as AriaTimeFieldProps
} from 'react-aria-components';
import { Time } from '@internationalized/date';
import { cva, VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from 'v5/platform/dom/cn';
import type { InputSize, InputVariant } from './Input';
import { focusRings } from './_common/focusRing';
import { useTranslation } from 'react-i18next';

const timeInputWrapperVariants = cva(
  [
    'outline-none',
    'flex items-center w-full',
    'placeholder:text-fg-muted',
    'disabled:cursor-not-allowed aria-readonly:cursor-default disabled:opacity-50'
  ],
  {
    variants: {
      size: {
        xs: 'h-[20px] py-1',
        sm: 'h-9 py-1.5',
        md: 'h-12 py-2',
        lg: 'h-15 py-3',
        xl: 'h-17 py-4'
      },
      variant: {
        regular: [
          'rounded',
          'border border-border-default bg-bg-default text-fg-default',
          'not-aria-readonly:hocus:border-bg-inset',
          focusRings.focusWithin.normal
        ],
        inline: [
          'transition-all',
          'bg-transparent text-fg-default',
          'not-aria-readonly:focus:shadow-[inset_0_-2px_0_0_]',
          'shadow-[inset_0_-1px_0_0_]',
          'shadow-border-default',
          'not-aria-readonly:hover:shadow-bg-inset',
          'not-aria-readonly:focus:shadow-brand-300',
          'not-aria-readonly:hover:focus:shadow-brand-400'
        ],
        plain: [
          'transition-all',
          'bg-transparent text-fg-default',
          'not-aria-readonly:focus:shadow-[inset_0_-2px_0_0_]',
          'shadow-[inset_0_-1px_0_0_]',
          'shadow-transparent',
          'not-aria-readonly:hover:shadow-bg-inset',
          'not-aria-readonly:focus:shadow-brand-300',
          'not-aria-readonly:hover:focus:shadow-brand-400'
        ]
      }
    },
    compoundVariants: [
      {
        variant: ['regular', 'inline', 'plain'],
        size: 'xs',
        className: 'px-0'
      },
      {
        variant: ['regular', 'inline', 'plain'],
        size: 'sm',
        className: 'px-0'
      },
      {
        variant: ['regular', 'inline', 'plain'],
        size: 'md',
        className: 'px-0.5'
      },
      {
        variant: ['regular', 'inline', 'plain'],
        size: 'lg',
        className: 'px-1'
      },
      {
        variant: ['regular', 'inline', 'plain'],
        size: 'xl',
        className: 'px-1'
      }
    ],
    defaultVariants: {
      variant: 'regular',
      size: 'md'
    }
  }
);

const segmentVariants = cva(
  [
    'focus:outline-none focus:bg-brand-200/50',
    'font-variant-numeric:tabular-nums',
    'text-align:end'
  ],
  {
    variants: {
      size: {
        xs: 'text-sm rounded-sm px-0',
        sm: 'text-base py-0.5 rounded-sm px-0.5 -mx-0.25',
        md: 'text-base py-0.5 rounded-sm px-1 -mx-0.5',
        lg: 'text-lg py-1 rounded px-1 -mx-0.5',
        xl: 'text-xl py-2 rounded px-1 -mx-0.5'
      }
    }
  }
);

export interface TimeInputProps
  extends Omit<AriaTimeFieldProps<Time>, 'value' | 'onChange' | 'size'>,
    VariantProps<typeof timeInputWrapperVariants> {
  value?: Time | null; // Accepts Time object from @internationalized/date
  onChange?: (value: Time | null) => void; // Returns Time object
  size?: InputSize;
  variant?: InputVariant;
  className?: string;
  readOnly?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export const TimeInput = React.forwardRef<HTMLDivElement, TimeInputProps>(
  (
    {
      value,
      onChange,
      size,
      variant,
      className,
      readOnly,
      placeholder,
      disabled,
      granularity = 'minute',
      hourCycle,
      ...props
    },
    ref
  ) => {
    return (
      <TimeField
        value={value ?? null}
        onChange={onChange || (() => {})}
        granularity={granularity}
        hourCycle={hourCycle}
        isReadOnly={readOnly}
        isDisabled={disabled}
        shouldForceLeadingZeros={true}
        {...props}
      >
        <AriaDateInput
          ref={ref}
          className={cn(
            timeInputWrapperVariants({ variant, size }),
            'inline-flex items-center gap-0.5',
            className
          )}
        >
          {segment => <TimeSegment segment={segment} size={size || 'md'} />}
        </AriaDateInput>
      </TimeField>
    );
  }
);

TimeInput.displayName = 'TimeInput';

interface TimeSegmentProps {
  segment: Parameters<
    React.ComponentProps<typeof AriaDateInput>['children']
  >[0];
  size: InputSize;
}

function TimeSegment({ segment, size }: TimeSegmentProps) {
  // Hide literal segments that are empty or only whitespace
  const isEmptyLiteral =
    segment.type === 'literal' && (!segment.text || segment.text.trim() === '');

  return (
    <AriaDateSegment
      segment={segment}
      className={cn(
        segmentVariants({ size }),
        segment.type === 'literal' && 'pointer-events-none px-1',
        segment.isPlaceholder && 'text-fg-muted italic',
        isEmptyLiteral && 'hidden'
      )}
    />
  );
}
